/**
 * DynamicConditionParamPopover Component
 *
 * Popover form for editing dynamic condition parameters.
 * Supports array types via comma-separated string input.
 * Uses a portal to render above all other content, positioned relative to anchor.
 */

import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { DYNAMIC_CONDITION_REGISTRY, DynamicParamSchema } from "../../../../../config/signals";
import { InfoTooltip } from "../../../../../components/common/InfoTooltip";
import styles from "./SignalParamPopover.module.css"; // Reuse same styles

interface DynamicConditionParamPopoverProps {
  conditionType: string;
  params: Record<string, unknown>;
  onParamsChange: (params: Record<string, unknown>) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  currentLegIndex?: number;
}

export const DynamicConditionParamPopover: React.FC<DynamicConditionParamPopoverProps> = ({
  conditionType,
  params,
  onParamsChange,
  onClose,
  anchorRef,
  currentLegIndex,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const schema = DYNAMIC_CONDITION_REGISTRY[conditionType];
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [validationError, setValidationError] = useState<string | null>(null);

  // Calculate position based on anchor element
  useEffect(() => {
    const updatePosition = () => {
      if (!anchorRef.current || !popoverRef.current) return;

      const rect = anchorRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();

      // Position above the anchor (subtract popover height + gap)
      const top = rect.top - popoverRect.height - 4;

      // Position to the left (align right edge of popover with right edge of anchor)
      const left = rect.right - popoverRect.width;

      setPosition({
        top: Math.max(8, top), // Ensure at least 8px from top of viewport
        left: Math.max(8, left), // Ensure at least 8px from left of viewport
      });
    };

    // Initial position update
    updatePosition();

    // Update again after a brief delay to account for popover rendering
    const timeoutId = setTimeout(updatePosition, 0);

    // Find scrollable ancestor and listen for scroll events
    let scrollableAncestor: HTMLElement | null = anchorRef.current;
    while (scrollableAncestor) {
      const style = window.getComputedStyle(scrollableAncestor);
      if (style.overflow === 'auto' || style.overflow === 'scroll' ||
          style.overflowY === 'auto' || style.overflowY === 'scroll') {
        break;
      }
      scrollableAncestor = scrollableAncestor.parentElement;
    }

    if (scrollableAncestor) {
      scrollableAncestor.addEventListener('scroll', updatePosition);
    }
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      clearTimeout(timeoutId);
      if (scrollableAncestor) {
        scrollableAncestor.removeEventListener('scroll', updatePosition);
      }
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorRef]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(target);
      const isOutsideAnchor = anchorRef.current && !anchorRef.current.contains(target);

      if (isOutsidePopover && isOutsideAnchor) {
        onClose();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, anchorRef]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Run validation when params change externally
  // MUST be before early returns to satisfy React Hooks rules
  useEffect(() => {
    if (schema?.validate) {
      const result = schema.validate(params, currentLegIndex);
      if (result !== true) {
        setValidationError(result);
      } else {
        setValidationError(null);
      }
    }
  }, [params, currentLegIndex, schema]);

  if (!schema) return null;

  const paramEntries = Object.entries(schema.params);
  if (paramEntries.length === 0) {
    return createPortal(
      <div
        ref={popoverRef}
        className={styles.popover}
        style={{ top: position.top, left: position.left }}
      >
        <div className={styles.noParams}>No parameters required</div>
      </div>,
      document.body
    );
  }

  const handleParamChange = (key: string, value: unknown) => {
    const newParams = { ...params, [key]: value };
    onParamsChange(newParams);

    // Run validation if schema has a validator
    if (schema.validate) {
      const result = schema.validate(newParams, currentLegIndex);
      if (result !== true) {
        setValidationError(result);
      } else {
        setValidationError(null);
      }
    }
  };

  return createPortal(
    <div
      ref={popoverRef}
      className={styles.popover}
      style={{ top: position.top, left: position.left }}
    >
      <div className={styles.header}>
        <span className={styles.title}>{schema.label}</span>
      </div>
      <div className={styles.content}>
        {paramEntries.map(([key, paramSchema]) => (
          <DynamicParamInput
            key={key}
            paramKey={key}
            schema={paramSchema}
            value={params[key]}
            onChange={(value) => handleParamChange(key, value)}
          />
        ))}
        {validationError && (
          <div className={styles.error} style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            {validationError}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

// Individual parameter input renderer for dynamic conditions
interface DynamicParamInputProps {
  paramKey: string;
  schema: DynamicParamSchema;
  value: unknown;
  onChange: (value: unknown) => void;
}

const DynamicParamInput: React.FC<DynamicParamInputProps> = ({ paramKey, schema, value, onChange }) => {
  // For array types, we store the raw string in local state while editing
  // This allows typing commas without immediate parsing
  const [localValue, setLocalValue] = useState<string>(() => {
    if (schema.isArray && Array.isArray(value)) {
      return value.join(", ");
    }
    return (value as string | number | undefined)?.toString() ?? "";
  });

  // Track if user is actively editing to prevent external sync overwriting input
  const [isEditing, setIsEditing] = useState(false);

  // Only sync from external value when NOT actively editing
  useEffect(() => {
    if (!isEditing) {
      if (schema.isArray && Array.isArray(value)) {
        setLocalValue(value.join(", "));
      } else if (value !== undefined && value !== null) {
        setLocalValue(value.toString());
      } else {
        setLocalValue("");
      }
    }
  }, [value, schema.isArray, isEditing]);

  const handleChange = (inputValue: string) => {
    setLocalValue(inputValue);

    if (schema.isArray) {
      // Parse comma-separated values to array on each change
      // Allow empty strings temporarily while typing
      if (inputValue.trim() === "") {
        onChange([]);
      } else {
        // Split by comma and process each value
        const parts = inputValue.split(",");
        const values: (number | string)[] = [];

        for (const part of parts) {
          const trimmed = part.trim();
          if (trimmed === "") continue; // Skip empty parts (allows trailing comma)

          if (schema.type === "integer") {
            const num = parseInt(trimmed, 10);
            if (!isNaN(num)) {
              values.push(num);
            }
          } else {
            values.push(trimmed);
          }
        }
        onChange(values);
      }
    } else {
      // Single value
      if (inputValue === "") {
        onChange(undefined);
      } else {
        // Convert to appropriate type
        if (schema.type === "integer") {
          const num = parseInt(inputValue, 10);
          onChange(isNaN(num) ? undefined : num);
        } else if (schema.type === "number") {
          const num = parseFloat(inputValue);
          onChange(isNaN(num) ? undefined : num);
        } else {
          onChange(inputValue);
        }
      }
    }
  };

  return (
    <div className={styles.paramRow}>
      <label className={styles.label}>
        {schema.label}
        {schema.required && <span className={styles.required}>*</span>}
        {schema.description && <InfoTooltip text={schema.description} />}
      </label>
      <input
        type="text"
        className={styles.input}
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setIsEditing(true)}
        onBlur={() => setIsEditing(false)}
        placeholder={schema.placeholder}
      />
    </div>
  );
};

